import { find,
  get,
  forEach,
  includes,
  toLower,
  noop,
  startsWith,
  isString,
  isObject } from 'lodash';
import { utils, getConversationColor as innerGetConversationColor, moment } from 'lp-vue-components';
import { AppFactory, i18n, Logger, sessionManager } from 'vue-infra';
import { initialize } from 'launchdarkly-js-client-sdk';
import linkifyHtml from 'linkify-html';
import * as linkify from 'linkifyjs';
import HtmlSanitizerUtils from 'lp-html-sanitizer/src/HtmlSanitizerUtils';// eslint-disable-line import/no-extraneous-dependencies
import { MESSAGE_STATUS_LEVELS, NAMESPACE } from '@/common/consts';
import Currency from '@/common/currency';
import { replaceSpecificElement } from '@/common/sanitizer';
import pkg from '../../package.json';

/**
 * Function to extract the sub domain from any URL
 * @param url
 * @return {String}
 */
export const getSubDomain = (url) => {
  const domainRegEx = new RegExp(/(http{1}s{0,1}?:\/\/)([^/?]+)(\/?)/gi);
  let matches;
  let domain = null;
  if (startsWith(url, 'http')) {
    matches = domainRegEx.exec(url);
  } else {
    matches = domainRegEx.exec(window.location.href);
  }
  if (matches && matches.length >= 3 && matches[2] !== '') {
    domain = matches[1].toLowerCase() + matches[2].toLowerCase(); // 0 - full match 1- HTTPS 2- domain
  }
  return domain;
};

export const empty = noop;

export const getServiceBaseURI = (serviceName, csdsRecords) => {
  const service = find(csdsRecords, item => item.service === serviceName);
  return service ? service.baseURI : null;
};

export function getFeatureFlagsLD(clientSiteId) {
  return new Promise((resolve, reject) => {
    try {
      const user = {
        key: '', // This user obj is just for the api
      };
      const client = initialize(clientSiteId, user);
      client.on('ready', () => {
        resolve(client.allFlags());
      });
    } catch (exception) {
      reject(exception);
    }
  });
}

export const resolveVersionFromResourceKey = (resource) => {
  const urlResolver = window.LE.urlResolver;
  const apiResources = urlResolver.apiResources;
  const apiResource = get(apiResources, resource);
  return apiResource && apiResource.version;
};

export const getConcatStrings = (values) => {
  let concatValue = '';
  forEach(values, (value) => {
    if (value && value !== 'undefined') {
      concatValue = concatValue ? `${concatValue} ${value}` : value;
    }
  });
  return concatValue;
};

export const filterObject = (collection, predicate) => {
  const result = {};

  forEach(collection, (item, key) => {
    if (predicate(item)) {
      result[key] = item;
    }
  });

  return result;
};

export const convertArrayToMap = (array, idAttr) => {
  const map = {};

  array.forEach((item) => {
    map[item[idAttr]] = item;
  });

  return map;
};

export const getInitials = (str) => {
  const splittedStr = str.split(' ');
  let finalStr = splittedStr[0].charAt(0);
  if (splittedStr.length > 1) {
    // if we have more than 2 words, add the first letter of the second word
    finalStr += splittedStr[1].charAt(0);
  } else if (splittedStr[0].length > 1) {
    // if we have only one word, and it has more than 2 characters, add the second character
    finalStr += splittedStr[0].charAt(1);
  }
  return finalStr.toUpperCase();
};

export const getDateRangeInfo = ({
  fromDate, toDate, isToDateNowTimeSelected, captionPrefix, strictDateFormat = false, dateOnly = false,
}) => {
  // LT = Local Time - to allow localization by Moment
  const dateFormat = dateOnly ? 'DD MMM YYYY' : 'DD MMM YYYY LT';

  // Moment configuration - set the above format for dates older than 48 hours
  const calendarFormat = {
    sameElse: dateFormat,
  };
  const calendarReferenceDay = 'sameElse';

  const fromDiff = moment().diff(fromDate, 'hours');
  const toDiff = moment().diff(toDate, 'hours');

  const fromDateDisplay = fromDiff < 48 && !strictDateFormat ? fromDate.calendar() : fromDate.calendar(calendarReferenceDay, calendarFormat);
  let toDateDisplay;
  if (isToDateNowTimeSelected) {
    toDateDisplay = i18n.t(`aw:${captionPrefix}_filter_by_time_to_now_caption`);
  } else {
    toDateDisplay = toDiff < 48 && !strictDateFormat ? toDate.calendar() : toDate.calendar(calendarReferenceDay, calendarFormat);
  }
  return `${fromDateDisplay} - ${toDateDisplay}`;
};

/**
 * Get Client Time according to the format used by the Logger:
 * YYYY-MM-DD HH:mm:ss,SSS
 *
 * @return {string}
 */
export function getLoggerClientTime() {
  return moment().format('YYYY-MM-DD HH:mm:ss,SSS');
}

export function isNumeric(value) {
  return !Number.isNaN(parseFloat(value));
}

export const isEmpty = (value) => {
  const s = value || '';
  return s.toString().length === 0;
};

export const getPriceWithCurrency = (amount, currencyCode) => {
  let moneyWithCurrency;
  if (amount) {
    const relevantCurrencyCode = currencyCode ||
      sessionManager.getAccountSettingValueByID(sessionManager.ACCOUNT_SETTINGS_ID.CURRENCY, Currency.defaultCurrency);
    const currencySymbol = get(Currency, ['currencyCodes', relevantCurrencyCode, 'symbol']);
    const separatorSpace = currencySymbol && currencySymbol.length === 1 ? '' : ' ';
    moneyWithCurrency = currencySymbol ? `${currencySymbol}${separatorSpace}${amount}` : `${amount} ${currencyCode}`;
  }
  return moneyWithCurrency;
};

export const generateUUID = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  const r = Math.random() * 16 | 0; // eslint-disable-line no-bitwise
  const v = c === 'x' ? r : (r & 0x3 | 0x8); // eslint-disable-line no-bitwise, no-mixed-operators
  return v.toString(16);
});

/**
 * Trasform a given set of charcters into a hash number
 * @param {*} value
 * @returns {String}
 */
export const toHashCode = utils.toHashCode;
export const detectTextDirection = utils.detectTextDirection;
export const getConversationColor = innerGetConversationColor;

export const isIE = () => {
  let result = false;
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');

  if (msie > 0 || !!ua.match(/Trident.*rv:11\./)) {
    result = true;
  }
  return result;
};

export const isEdge = () => {
  const navigator = window.navigator;
  return navigator && ((navigator.appName === 'Netscape') &&
    (navigator.userAgent && includes(navigator.userAgent, 'Edge/')));
};

export const isFirefox = () => includes(toLower(navigator.userAgent), 'firefox');

export const loadScripts = async (array) => {
  const scriptTagGenerator = (src, handler, errorHandler) => {
    const script = document.createElement('script');
    script.src = src;

    script.onload = script.onreadystatechange = () => { // eslint-disable-line no-multi-assign
      script.onreadystatechange = script.onload = null; // eslint-disable-line no-multi-assign
      handler();
    };

    script.onerror = (e) => {
      errorHandler(e);
    };

    const head = document.getElementsByTagName('head')[0];
    (head || document.body).appendChild(script);
  };

  return new Promise((resolve, reject) => {
    const run = () => {
      if (array.length !== 0) {
        scriptTagGenerator(array.shift(), run, reject);
      } else {
        resolve(0);
      }
    };
    run();
  });
};

export const getTheme = currentActiveTheme => (isString(currentActiveTheme) ? currentActiveTheme : get(currentActiveTheme, 'keyName'));

export const getStringified = data => (isObject(data) ? JSON.stringify(data) : data);

export function shouldUpdateMessageStatus(newStatus, oldStatus) {
  return MESSAGE_STATUS_LEVELS[newStatus] &&
    (!MESSAGE_STATUS_LEVELS[oldStatus] || MESSAGE_STATUS_LEVELS[oldStatus] < MESSAGE_STATUS_LEVELS[newStatus]);
}

export function parseSdeCappingSettings(settings) {
  const parsed = [];
  forEach(settings, (setting, sdeKey) => {
    let sdeSetting = setting.quantity ? `:${setting.quantity}` : '';
    sdeSetting += setting.order ? `:${setting.order}` : '';
    if (sdeSetting) {
      parsed.push(sdeKey + sdeSetting);
    }
  });

  return parsed.join(',');
}

export function copyMessage(message) {
  const { clipboard: clipboardAPI } = navigator;
  const execCommandAPI = document.execCommand;
  const isExecCopySupported = execCommandAPI && document.execCommand('copy');

  let retValue;
  try {
    if (clipboardAPI) {
      // eslint-disable-next-line promise/prefer-await-to-then,promise/catch-or-return
      clipboardAPI.writeText(message).then(() => {
        retValue = Promise.resolve();
      });
    } else if (isExecCopySupported) {
      const el = document.createElement('textarea');
      el.value = message;
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      retValue = Promise.resolve();
    }
  } catch (error) {
    retValue = Promise.reject(error);
  }
  return retValue;
}
export function renderLink(str, isRunSanitizeHtml, isAgent) {
  if (str && isString(str)) {
    let msg = str;
    if (isRunSanitizeHtml) {
      msg = HtmlSanitizerUtils.sanitizeHtml(msg).cleanText;
    }
    let className = 'le-markdown-link';
    if (!isAgent) {
      msg = replaceSpecificElement(msg, 'a', 'span');
      className = 'le-link';
    }
    return linkifyHtml(msg, {
      defaultProtocol: 'https', target: '_blank', rel: 'noopener noreferrer', className: `link ${className}`,
    });
  }
  return str;
}
export function isHasLink(str) {
  if (isString(str)) {
    const hasLink = linkify.find(str);
    if (hasLink.length > 0) {
      return true;
    }
  }
  return false;
}

export function getLogger(context) {
  return Logger.getLogger(context, 'AgentWorkspace');
}

export function sendErrorLogs(logger, namespace, message, payload) {
  const store = AppFactory.store;
  const getSessionValue = store.getters['session/getValue'];
  const accountId = getSessionValue('accountId');
  const agentId = getSessionValue('userId');
  const loginName = getSessionValue('loginName');
  const phase = get(window, ['leConfiguration', 'lp_phase']);
  const environment = get(window, ['leConfiguration', 'lp_grid']);
  const finalNamespace = `${NAMESPACE.NEW_AGENT_WORKSPACE}: ${namespace}`;
  const data = {
    clientTime: getLoggerClientTime(),
    accountId,
    agentId,
    loginName,
    phase,
    environment,
    moduleVersion: `${pkg.version}`,
    data: {
      ...payload,
    },
  };

  logger.error(message, finalNamespace, data);
}
